import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link, useParams } from "react-router-dom";
import useLocalStorage from "use-local-storage";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import "../../Home/components/Main.scss";
import "./Writing.scss";

const RateVaryingArticle = () => (
  <div className="section-text article-body">
    <p className="article-title">The varied rate Poisson Process and MMPP</p>
    <p className="article-date">June 2024</p>

    <p>
      {
        "Counting processes have a variety of use cases in modelling networks, queues, communications and other real-world systems. The most classic example being the Poisson process, a natural choice to model an uncoordinated stream of discrete events in continuous time. This post will go over the Markov Modulated Poisson Process, a counting process based on the Poisson process which attains greater flexibility due to a stochastic rate function."
      }
    </p>

    <p className="article-heading">Counting Processes</p>
    <p>
      {"We recall that a counting process "}
      <InlineMath math="\{N_t, t \geq 0\}" />
      {" is one where:"}
    </p>
    <ul className="article-list">
      <li>
        <InlineMath math="N_t \in \mathbb{Z}_{\geq 0}" />
      </li>
      <li>
        <InlineMath math="P(N_0 = 0) = 1" />
      </li>
      <li>
        {"for "}
        <InlineMath math="0 \leq s < t,\ N_t - N_s \geq 0" />
        {" represents the number of events in interval "}
        <InlineMath math="(s, t]" />
      </li>
      <li>{"which is right-continuous with left-hand limits"}</li>
    </ul>

    <p className="article-heading">Rate Varying Poisson Process</p>
    <p>
      {
        "For a discrete time process with independent increments we denote a rate varying Poisson process "
      }
      <InlineMath math="N(t \mid \lambda(t))" />
      {" to be one with parameterized density of:"}
    </p>
    <BlockMath math="P(N(t \mid \lambda(t)) = n) = \frac{(\lambda(t) \cdot t)^n}{n!} e^{-\lambda(t) \cdot t}" />
    <p>
      {"where "}
      <InlineMath math="\lambda(t)" />
      {" is some deterministic function "}
      <InlineMath math="\lambda : T \to \mathbb{R}_{\geq 0}" />
      {"."}
    </p>
    <p>
      {"The rate function "}
      <InlineMath math="\lambda" />
      {" is also typically referred to as the intensity."}
    </p>

    <p className="article-heading">Markov Modulated Poisson Process (MMPP)</p>
    <p>
      {
        "The MMPP is simply then a particular case of the rate varying Poisson process, particularly where the intensity is stochastic itself. More precisely, its intensity is the state of an independently progressing continuous-time Markov chain. We have that for a process "
      }
      <InlineMath math="\{X_t, t \geq 0\}" />
      {", we call it a MMPP if:"}
    </p>
    <BlockMath math="X_t = N(t \mid \lambda(t) = Y_t)" />
    <p>
      {"where "}
      <InlineMath math="\{Y_t, t \geq 0\}" />
      {" is a continuous-time Markov chain with infinitesimal generator:"}
    </p>
    <BlockMath math="Q = \begin{pmatrix} q_{11} & q_{12} & \cdots & q_{1n} \\ q_{21} & q_{22} & \cdots & q_{2n} \\ \vdots & \vdots & \ddots & \vdots \\ q_{n1} & q_{n2} & \cdots & q_{nn} \end{pmatrix}" />
    <BlockMath math="q_{ij} \geq 0 \ \forall\, i \neq j \quad \text{and} \quad 0 \leq q_{ii} < \infty \quad \text{and} \quad \sum_{j=1}^{k} q_{ij} = 0" />
    <p>
      {"It follows that "}
      <InlineMath math="X_t" />
      {" is also Markov, i.e. for probability triple "}
      <InlineMath math="(\Omega, \mathcal{F}_t, \mathbb{P})" />
      {" we have:"}
    </p>
    <BlockMath math="P(X_t \in A \mid \mathcal{F}_t) = P(X_t \in A \mid X_s)" />

    <p className="article-heading">An Application</p>
    <p>
      {
        "Suppose we're an airline company modeling the number of tickets sales from our website on a given date. We recognize that demand comes in surge periods, and we categorize the three general times of demand periods as "
      }
      <em>low demand</em>
      {", "}
      <em>medium demand</em>
      {" and "}
      <em>surge demand</em>
      {
        ". We can use a MMPP as our count model, where the intensity, which varies stochastically, corresponds to one of these three periods. We use the following continuous-time Markov chain as an example:"
      }
    </p>

    <figure className="article-figure">
      <img
        src="/writing/rate-varying/markov_chain_diagram.svg"
        alt="Continuous-time Markov chain with three states, transition rates between them."
      />
      <figcaption>
        Continuous-time Markov chain over the three demand states.
      </figcaption>
    </figure>

    <p>{"which has corresponding infinitesimal generator matrix:"}</p>
    <BlockMath math="Q = \begin{bmatrix} -3 & 2 & 1 \\ 3 & -5 & 2 \\ 5 & 3 & -8 \end{bmatrix}" />
    <p>{"and supply our intensity set:"}</p>
    <BlockMath math="\vec{\lambda} = \langle \lambda_1 = 25, \lambda_2 = 100, \lambda_3 = 500 \rangle" />
    <p>
      {
        "in the units of count per day. We then run a simple simulation to generate a sample path for illustration:"
      }
    </p>

    <figure className="article-figure">
      <img
        src="/writing/rate-varying/mmpp_simulation.svg"
        alt="Simulated MMPP sample path: cumulative ticket sales and the underlying intensity state process over time."
      />
      <figcaption>
        Airline Ticket Sales MMPP Simulation — cumulative sales and the
        underlying state process.
      </figcaption>
    </figure>

    <p>
      {
        "We see that in this case the MMPP is able to capture the effects of discrete 'regime shift' periods quite well."
      }
    </p>
    <p>
      {
        "While MMPPs are powerful, they also come with increased complexity in parameter estimation and analysis. We require the parametric estimate of both "
      }
      <InlineMath math="(\vec{\lambda}, Q)" />
      {", where "}
      <InlineMath math="\vec{\lambda} = (\lambda_1, \ldots, \lambda_n)" />
      {" is the vector of intensity rates and "}
      <InlineMath math="Q = (q_{ij})_{i,j=1}^{n}" />
      {
        " is the infinitesimal generator matrix of the underlying Markov process. The choice to use an MMPP should be balanced against the need for model simplicity and the available data for parameter inference."
      }
    </p>
  </div>
);

const AnticlusteringArticle = () => (
  <div className="section-text article-body">
    <p className="article-title">
      Anticlustering and Multi-Objective Optimization
    </p>
    <p className="article-date">September 2024</p>

    <p>
      {
        "Traditionally in the realm of data science and optimization, we often encounter problems that require grouping similar items together. The task of finding these groups and maximizing homogeneity is known as clustering. However, there are scenarios where we need to do the opposite - create groups that are as heterogeneous as possible, which leads to the emergence of anticlustering techniques."
      }
    </p>
    <p>
      {
        "The fundamental principle of anticlustering aligns with the concept of intra-group dissimilarity maximization, contrasting sharply with conventional clustering techniques that aim to minimize within-group differences. Although this may simply seem like a clustering task within an inverted objective function, anticlustering presents a unique set of difficulties that make it more computationally complex. Intuitively, it should be easier to spatially find groups in a Euclidean space that are close to one another rather than the groups that contain points furthest from one another: the partitioning of the space can be clean in the former but rarely so in the latter."
      }
    </p>

    <p className="article-heading">Combinatorial Approaches</p>
    <p>
      {
        "The anticlustering problem can be framed in a variety of ways; one of them notably is the partitioning of a fully connected graph. In this context, we're looking for a set of subgraphs that maximize our dissimilarity across this graph partition. To take a combinatorial approach, we must form edge weights between the entries in our dataset to represent them as nodes in a fully connected graph."
      }
    </p>
    <p>{"Given a dataset:"}</p>
    <BlockMath math="D = \{(i_1, \mathbf{x}_1), (i_2, \mathbf{x}_2), \ldots, (i_n, \mathbf{x}_n)\}" />
    <p>{"where:"}</p>
    <ul className="article-list">
      <li>
        <InlineMath math="i_k \in \mathbb{N}" />
        {" is a unique identifier for each data point"}
      </li>
      <li>
        <InlineMath math="\mathbf{x}_k \in \mathbb{R}^d" />
        {" is a "}
        <InlineMath math="d" />
        {"-dimensional vector of auxiliary features"}
      </li>
    </ul>
    <p>
      {"We require a distance measure "}
      <InlineMath math="d: \mathbb{R}^d \times \mathbb{R}^d \rightarrow \mathbb{R}_{\geq 0}" />
      {" operating on pairs of feature vectors where:"}
    </p>
    <ul className="article-list">
      <li>
        <InlineMath math="d(\mathbf{x}, \mathbf{y}) \geq 0" />
      </li>
      <li>
        <InlineMath math="d(\mathbf{x}, \mathbf{y}) = 0" />
        {" if and only if "}
        <InlineMath math="\mathbf{x} = \mathbf{y}" />
      </li>
      <li>
        <InlineMath math="d(\mathbf{x}, \mathbf{y}) = d(\mathbf{y}, \mathbf{x})" />
      </li>
    </ul>
    <p>
      {"For example, "}
      <InlineMath math="\mathbf{x}_k" />
      {
        " could represent attributes such as age, income, or geographical coordinates, or even categorical variables like occupation or industry. Common choices for "
      }
      <InlineMath math="d" />
      {
        " include Euclidean distance, cosine dissimilarity for numerical variables, or metrics such as overlap distance or Jensen-Shannon divergence for categorical variables."
      }
    </p>
    <p>
      {"With such a metric defined, we can formally specify a fully connected weighted graph "}
      <InlineMath math="G = (V, E, w)" />
      {" where:"}
    </p>
    <ul className="article-list">
      <li>
        <InlineMath math="V = \{1, \ldots, n\}" />
        {" is the set of vertices (corresponding to our data points)"}
      </li>
      <li>
        <InlineMath math="E = \{\{i, j\} : i, j \in V, i \neq j\}" />
        {" is the set of all possible edges"}
      </li>
      <li>
        <InlineMath math="w: E \rightarrow \mathbb{R}_{\geq 0}" />
        {" is the weight function defined as "}
        <InlineMath math="w(\{i, j\}) = d(\mathbf{x}_i, \mathbf{x}_j)" />
        {" for all "}
        <InlineMath math="\{i, j\} \in E" />
      </li>
    </ul>

    <p className="article-heading">A Motivating Example</p>
    <p>
      {
        "Suppose we're conducting a mass hiring operation for a company. We have many interviewers and many more applicants. The interviewers have varied capacities to interview and can only interview a certain number of applicants in this hiring season. We have some high level data on the applicants, consider the following dataset:"
      }
    </p>
    <div className="article-table-wrap">
      <table className="article-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>University</th>
            <th>Year of Study</th>
            <th>Major</th>
            <th>Grade Average</th>
            <th>Total Credits</th>
            <th>Attendance Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Lisa Dodson</td>
            <td>University of Montreal</td>
            <td>Fourth Year</td>
            <td>Psychology</td>
            <td>0.5328</td>
            <td>270.3</td>
            <td>0.666</td>
          </tr>
          <tr>
            <td>Joshua Cox</td>
            <td>University of Calgary</td>
            <td>Second Year</td>
            <td>Mathematics</td>
            <td>0.9212</td>
            <td>263</td>
            <td>0.949</td>
          </tr>
          <tr>
            <td>Danny Le</td>
            <td>Dalhousie University</td>
            <td>First Year</td>
            <td>Physics</td>
            <td>0.2476</td>
            <td>9.9</td>
            <td>0.635</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      {
        "We now may want to form groupings of applicants to be assigned to each interviewer. To reduce systematic bias, we desire that the interviewers do not interview too many applicants from similar backgrounds to ensure they're exposed to a variety of experiences. This is where anticlustering comes in."
      }
    </p>
    <p>
      {
        "We now require a distance metric to form our dissimilarity matrix. In this case we can apply Gower's distance to handle the combination of numerical and categorical data. Applying the metric to this dataset yields the following dissimilarity matrix:"
      }
    </p>
    <div className="article-table-wrap">
      <table className="article-table">
        <thead>
          <tr>
            <th>Student</th>
            <th>Lisa Dodson</th>
            <th>Joshua Cox</th>
            <th>Danny Le</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Lisa Dodson</td>
            <td>0.0000</td>
            <td>0.7866</td>
            <td>0.7889</td>
          </tr>
          <tr>
            <td>Joshua Cox</td>
            <td>0.7866</td>
            <td>0.0000</td>
            <td>0.9960</td>
          </tr>
          <tr>
            <td>Danny Le</td>
            <td>0.7889</td>
            <td>0.9960</td>
            <td>0.0000</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      {"Such dissimilarity matrices, which represent our graph "}
      <InlineMath math="G" />
      {", will be the ideal input for our anticlustering algorithm."}
    </p>

    <p className="article-heading">Multi-Objective Optimization</p>
    <p>
      {
        "In optimization problems, we're occasionally concerned with more than one objective function. Commonly, these objectives are conflicting, meaning that there is generally no single solution that optimizes them all simultaneously. In such cases, we need to find a set of trade-off solutions that collectively represent the best possible outcomes."
      }
    </p>
    <p>
      {
        "Borrowing from ideas of game theory, we often use the concept of Pareto optimality. A solution is Pareto optimal if no objective can be improved without degrading at least one other objective. There can generally be more than one solution that exists that remains Pareto optimal, these set of solutions form what is known as the Pareto set."
      }
    </p>
    <p>
      {
        "The solutions in the Pareto set are non-dominated, meaning no solution in this set can be improved in any objective without degrading at least one other objective. Formally, we define the Pareto set "
      }
      <InlineMath math="P" />
      {" for a "}
      <InlineMath math="k" />
      {" objective optimization problem as:"}
    </p>
    <BlockMath math="P = \{ \chi_i = \langle \theta_1, \ldots \theta_k \rangle, \theta_i \in \mathbb{R} \}" />
    <p>
      {"where "}
      <InlineMath math="\chi_i" />
      {" is a Pareto optimal solution and "}
      <InlineMath math="\theta_j \in \mathbb{R}" />
      {" is a parameter of interest for the "}
      <InlineMath math="j" />
      {"-th objective function "}
      <InlineMath math="f_j" />
      {". The Pareto set requires that for any two solutions "}
      <InlineMath math="\chi_a" />
      {" and "}
      <InlineMath math="\chi_b" />
      {" in the Pareto set "}
      <InlineMath math="P" />
      {", neither dominates the other. In other words:"}
    </p>
    <BlockMath math="\nexists \chi_a, \chi_b \in P \text{ such that } \forall j \in \{1, \ldots, k\}: f_j(\chi_a) \geq f_j(\chi_b)" />
    <p>{"and"}</p>
    <BlockMath math="\exists j \in \{1, \ldots, k\}: f_j(\chi_a) > f_j(\chi_b)" />
    <p>
      {"Where "}
      <InlineMath math="f_j" />
      {
        " is the "
      }
      <InlineMath math="j" />
      {
        "-th objective function. In other words, for any pair of solutions in the Pareto set, each is better than the other in at least one objective, ensuring that all solutions in the set are non-dominated with respect to one another. We can graphically represent the Pareto set with a frontier demonstrating the trade-offs between the various objectives, called the Pareto front."
      }
    </p>
    <p>
      {
        "In the case of combinatorial optimization for anticlustering, the solutions "
      }
      <InlineMath math="\chi_i" />
      {" correspond to partitions of the graph "}
      <InlineMath math="\pi_i" />
      {". Moving forward we will denote:"}
    </p>
    <ul className="article-list">
      <li>
        <InlineMath math="n \in \mathbb{N}" />
        {" as the number of data points in the dataset"}
      </li>
      <li>
        <InlineMath math="\langle N_1, \ldots N_k \rangle \mid N_i \in \mathbb{N}" />
        {" as the group sizes for all partitions where there are "}
        <InlineMath math="k \in \mathbb{N}" />
        {" many groups"}
      </li>
      <li>
        {"A partition "}
        <InlineMath math="\pi = \{S_1, \ldots, S_k\}" />
        {" of the data points into "}
        <InlineMath math="k" />
        {" groups where "}
        <InlineMath math="S_i" />
        {" is the "}
        <InlineMath math="i" />
        {"-th group/sub-graph"}
      </li>
    </ul>

    <p className="article-heading">
      Multi-Restart Bicriterion Pairwise Interchange
    </p>
    <p>
      {
        "The MBPI algorithm, presented by Brusco et al. in 2017, is a multi-objective combinatorial optimization algorithm for anticlustering. It falls in the class of stochastic search algorithms, which means that it uses a random source to facilitate exploration of the solution space. The algorithm is a bicriterion optimizer, in particular it seeks to maximize two metrics across a partition of the graph: the diversity and dispersion of the groups."
      }
    </p>
    <p>
      {
        "We define the diversity of a partition as the sum of the distance values within each group:"
      }
    </p>
    <BlockMath math="F_1(\pi) = \sum_{m=1}^k \sum_{(u < v) \in S_m} d_{uv}, \text{ for } 1 \leq m \leq k" />
    <p>
      {
        "This measure effectively sums the upper triangular elements of the sub-distance matrices formed by a partition, capturing the pairwise dissimilarities within the group. The dispersion of a partition is defined as the minimum of the dissimilarity values between groups:"
      }
    </p>
    <BlockMath math="F_2(\pi) = \min_{1 \leq m \leq k} \left( \min_{(u<v) \in S_m} {d_{uv}} \right)" />
    <p>
      {
        "The algorithm uses a weighted combination of these two objectives to evaluate the progress of a local search procedure, known as a single restart."
      }
    </p>
    <BlockMath math="Z(\pi) = \omega_1 F_1(\pi) + \omega_2 F_2(\pi)" />
    <p>
      {"where "}
      <InlineMath math="\omega_1, \omega_2 \in \mathbb{R}_{\geq 0}" />
      {" s.t. "}
      <InlineMath math="\omega_1 + \omega_2 = 1" />
      {
        " are weighting factors supplied by the user. The algorithm thus attempts to maximize the above while evaluating the dominance of candidate solutions with respect to the Pareto set."
      }
    </p>
    <p>
      {"Typically many anticlustering algorithms will simply focus on the diversity objective, giving "}
      <InlineMath math="\omega_2 = 0" />
      {
        ". However, this can lead to a significant unbalancing of certain groups, which is the motivation for the use of the dispersion objective. This is especially important in the case of the hiring problem, where we may desire that every group is balanced in terms of applicant dissimilarity."
      }
    </p>
    <p>
      {
        "If we considered simply optimizing the dispersion metric, we'd find a significant amount of ties in potential candidate solutions which can appear vastly different from one another. The diversity metric, on the other hand, will see less ties. Having both criteria present allows us to also evaluate the concordance of the two measures, providing a more nuanced set of insights regarding the graph in question."
      }
    </p>
    <p>{"The algorithm can be described as follows:"}</p>
    <pre className="article-code">
      <code>{`Input:
- A: Distance matrix
- R: Number of restarts
- n: Number of data points
- ⟨N₁, ..., Nₖ⟩: Desired group sizes for all 1 ≤ i ≤ k

Initialization:
- P: Pareto set, initially empty (contains non-dominated partitions)
- Generate initial random partitions of n data points into k groups, ensuring group sizes are respected.

Algorithm:

For each restart r from 1 to R:
    Set Up Random Weights:
        Select weights ω₁ and ω₂ such that 0 ≤ ω₁ ≤ 1 and ω₂ = 1 - ω₁.
        Weights are chosen randomly from a set of predefined options to ensure diverse trade-offs.

    Generate Initial Partition π:
        Randomly assign n data points to k groups, preserving group sizes based on ⟨N₁, ..., Nₖ⟩.
        This partition serves as the starting point for the pairwise interchange heuristic.

    Pairwise Interchange Local Search:
        Repeat until no improvements:
            For each pair of data points u and v (where u and v belong to different groups):
                Temporarily swap u from group Sᵢ to group Sⱼ.
                Recalculate the objective functions F₁(π) and F₂(π) (diversity and dispersion).
                Compute the weighted sum of the objectives Z(π) = ω₁*F₁(π) + ω₂*F₂(π).
                Check Pareto Dominance:
                    If the new partition π' improves upon Z* and is not dominated by any partition in the current Pareto set P:
                        Add π' to P.
                        Remove any dominated partitions from P (i.e., if π' dominates π, remove π from P).
                        Update Z* and set the improvement flag to True.
                    If π' is worse or dominated, revert the swap and retain the previous partition.

    Convergence and Restart:
        Once no further pairwise swaps yield improvements (i.e., no new π' dominates the current Pareto set):
            Increment the restart counter r and repeat the process from the beginning (with new random weights and a new partition).

Termination:
    After completing R restarts, return the Pareto set P.

Output:
- Return the Pareto set P, which contains all non-dominated partitions found across all restarts. Each partition in P represents a different trade-off between diversity and dispersion.`}</code>
    </pre>
    <p>
      {
        "The restart is thus the key mechanism which escapes local maxima. A restart begins with a new random partition, which the local search will be highly dependent on given pairwise interchanges are miniscule steps which do not drastically change the Kendall-Tau distance of a given partition."
      }
    </p>
    
    <p className="article-heading">Limitations</p>
    <p>
      {
        "While the MBPI algorithm offers a powerful approach to multi-objective partitioning, it's important to acknowledge its limitations:"
      }
    </p>
    <ul className="article-list">
      <li>
        {
          "Approximation: Given this is a heuristic algorithm, there is no guarantee of convergence to the Pareto set."
        }
      </li>
      <li>
        {
          "Sensitivity to Initial Conditions: The quality of solutions can be influenced by the initial random partitions and weight selections, potentially leading to variability in results across different runs."
        }
      </li>
      <li>
        {
          "Restart Sensitivity and Local Optima: As with many local search algorithms, MBPI does not guarantee finding the global optimum. The pairwise interchange and iterative local search may get trapped in local optima, potentially missing the true Pareto-efficient set."
        }
      </li>
      <li>
        {"Computational Intensity: The computational intensity of the MBPI is proportional to the number of restarts "}
        <InlineMath math="R" />
        {" and the number of nodes "}
        <InlineMath math="n" />
        {
          " in the graph. For large datasets or complex problems, we will typically need to run the algorithm with many restarts for sufficient exploration of the solution space, making the algorithm intractable at certain scales."
        }
      </li>
    </ul>
    <p>
      {
        "Given the problem at hand is one that is NP-hard, we can likely not expect to step away from a heuristic based approach for the time being. Potential approaches to address other shortcomings include parallelized implementations, adaptive restarts and intermediate heuristics or hybridization with other optimization techniques."
      }
    </p>

    <p className="article-heading">References</p>
    <ol className="article-list">
      <li>
        {
          "Brusco, M.J., Cradit, J.D. and Steinley, D. (2020), Combining diversity and dispersion criteria for anticlustering: A bicriterion approach. Br J Math Stat Psychol, 73: 375-396."
        }
      </li>
    </ol>
  </div>
);

const FXMarketStructureArticle = () => (
  <div className="section-text article-body">
    <p className="article-title">FX Market Structure</p>
    <p className="article-date">May 2025</p>

    <p className="article-heading">About this doc</p>
    <p>
      {
        "There is a glossary at the bottom. It's my distillation of a few days of FX market research; please reach out if anything is confusing."
      }
    </p>

    <p className="article-heading">FX Spot Markets</p>
    <p>
      {
        "Spot FX as a whole is decentralized; there is no universally agreed upon clearing house."
      }
    </p>
    <p>
      {
        "Most spot FX is T+2; except USD/CAD and a few others which are T+1. The FX market operates 24/5 across various electronic platforms and dealer networks. Trades can be executed via interdealer brokers (e.g. EBS, Refinitiv Matching), multi-bank trading platforms (e.g. FXall, 360T, Bloomberg FXGO), single-dealer platforms, or voice/chat channels for less liquid trades. The rise of electronic communication networks (ECNs) and request-for-quote (RFQ) platforms has driven high efficiency: in major currency pairs, most pricing is electronic and often automated via algorithms. Global banks (e.g. JPMorgan, Citi, UBS, Deutsche Bank) remain key market-makers, collectively providing much of the liquidity and credit that underpins FX trading. However, non-bank liquidity providers (proprietary trading firms like XTX Markets, Citadel Securities, etc.) have grown in influence, streaming competitive prices on electronic venues. These players benefit from prime brokerage arrangements that grant them access to the same anonymous trading venues as banks. Prime brokers (often large banks) act as intermediaries that allow clients (funds, retail brokers, fintechs) to trade with numerous counterparties under a single credit line. This structure has enabled broad market access but concentrates credit risk in prime banks."
      }
    </p>

    <p className="article-subheading">What venues exist for FX?</p>
    <ul className="article-list">
      <li>
        {
          "Refinitiv (LSEG) FX platforms: ~$100 billion/day in spot across Matching and FXall combined (peaking at $109B in Sep 2024), and about $378B/day in forwards/swaps. FXall is the leading multi-dealer platform for corporate/investor flow."
        }
      </li>
      <li>
        {
          "CME EBS: Roughly $60 billion/day average spot in 2024 (with highs above $70B), after a dip in 2023. CME is launching \"FX Spot+\" to integrate EBS more with its futures, indicating strategic importance."
        }
      </li>
      <li>
        {
          "Cboe FX (Hotspot): In the $40–50B/day range in spot. Notably strong in certain pairs and during US hours. Hit record volumes in April 2024 amid yen volatility."
        }
      </li>
      <li>
        {
          "FXSpotStream: ~$50B/day in 2024 (spot). It's a unique aggregated streaming RFQ service (a bank consortium that provides a single API for clients to get quotes from all 15+ member banks). It had a surge in Q4 2024 volumes (+25% from Q1), showing regained growth."
        }
      </li>
      <li>
        {
          "360T: ~$168B/day total in 2024 across products (spot + swaps). It had record non-spot (forward, swap) volumes – e.g. NDF volumes rose to $1.74B/day. 360T's spot component is smaller than its swaps but still significant (~$20–30B)."
        }
      </li>
      <li>
        {
          "Euronext FX (ex-FastMatch): On the order of ~$20B/day spot in 2024, after some growth. It had peaks in late 2024 with US election-related trading."
        }
      </li>
      <li>
        {
          "LMAX Exchange: A London-based FX ECN focused on funds/prop traders, known for cryptocurrency as well. FX ADV possibly in tens of billions (LMAX doesn't publicly report, but it's recognized as a top FX venue)."
        }
      </li>
      <li>
        {
          "Regional/local venues: e.g. FXMarkets TX (Singapore), Currenex (now part of State Street), and Integral each may handle single-digit billions daily. Bloomberg FXGO handles very large ticket notional (especially for corporate and central bank trades) but as an RFQ hub with no centralized tape, volumes aren't disclosed."
        }
      </li>
    </ul>
    <p>
      {
        "Importantly, a chunk of FX trading still happens via voice brokers and direct bilateral trades, especially for less liquid currencies or large hedge funds dealing directly with bank desks. Interdealer brokers like TP ICAP and BGC facilitate voice matching for pairs like USD/TRY, USD/KRW, or when big flows need discretion. The BIS 2022 survey indicated about 15% of FX turnover might still be via phone or unstructured methods, but this share is declining as e-trading reaches into those areas too."
      }
    </p>

    <p className="article-subheading">Risk types</p>
    <p>{"TLDR:"}</p>
    <ul className="article-list">
      <li>
        <em>For dealers:</em>
        {
          " A bank's FX trading desk measures market risk using metrics like Value-at-Risk (VaR) – e.g. a daily 99% VaR of $5 million means there's a 1% chance the desk loses more than $5m in a day based on historical volatility. Banks set position limits (e.g. cannot be more than $X net long in any currency) and stop-loss limits to control traders. They also perform stress tests simulating extreme moves (like a 10% overnight devaluation) to see potential losses and ensure they're within tolerances or covered by capital. Traders use tools like stop-loss orders to cut positions if the market goes against them beyond a point. Some firms also hedge dynamically – for instance, an importer with a large USD payable might buy a forward, removing its FX market risk and converting it to a known cost."
        }
      </li>
      <li>
        <em>For investors:</em>
        {
          " A global fund will measure FX exposure on its portfolio and may use FX VaR or scenario analysis (e.g. if USD drops 5%, portfolio value changes by Y). They may hedge part of their exposure to reduce this risk."
        }
      </li>
      <li>
        <em>For corporates:</em>
        {
          " The risk is that currency moves could reduce their local currency revenues or increase costs. They manage it by hedging as discussed – converting an uncertain future rate to a fixed rate via forwards/options, thus trading market risk for known cost (and possibly some premium cost)."
        }
      </li>
      <li>
        {
          "Market risk can also come from volatility: e.g. an options writer is at risk if volatility spikes (option values rise). They manage it by delta hedging (adjusting underlying positions as market moves) and using risk limits on \"greeks\" like vega (sensitivity to vol) and gamma (sensitivity of delta to spot changes)."
        }
      </li>
    </ul>
    <p>{"In more detail below."}</p>

    <p className="article-subheading">Credit risk (counterparty risk)</p>
    <p>
      {
        "In FX, this arises primarily in forward or swap contracts (where there's a period between trade and settlement) and when settling trades (one party could deliver funds and the other fails)."
      }
    </p>
    <ul className="article-list">
      <li>
        {
          "The worst historical example was Herstatt Bank in 1974: it received Deutschmarks in Frankfurt but failed before delivering dollars in New York (thus \"Herstatt risk\"). Settlement risk is largely addressed by CLS Bank's PvP mechanism, which now covers the bulk of major currency trades. For trades outside CLS, parties often limit exposure via bilateral payment netting and using correspondent banking arrangements that allow cancellation if the other fails to pay. See CLS section."
        }
      </li>
      <li>
        {
          "For forwards/derivatives, counterparty credit risk is mitigated by netting agreements (ISDA) and margin (collateral). The majority of interdealer FX forward and swap exposure is covered by daily variation margin under CSAs. If one party defaults, the other can close-out the net position and use collateral to cover losses."
        }
      </li>
      <li>
        {
          "Prime brokerage setups concentrate counterparty risk on the PB (the client trades with many parties but the PB is the legal counterparty to all those trades). The PB manages this by requiring initial margin and default fund contributions. E.g. a prime broker will stop a client from trading or liquidate positions if their margin falls too low, thus preventing a scenario where the client can't pay the counterparties."
        }
      </li>
      <li>
        {
          "Non-bank participants: Corporates usually have credit lines with banks for forwards. If a corporate approaches its credit limit (mark-to-market losses on forwards), the bank may ask for early settlement or margin. Some corporates preemptively post collateral for large long-dated hedges to reduce bank credit charges."
        }
      </li>
      <li>
        {
          "A metric regulators watch is CVA (Credit Valuation Adjustment) – essentially the market value of counterparty credit risk. Banks must hold capital for potential CVA losses (the risk that a counterparty's credit worsens and the derivative MTM would have to be adjusted). This has incentivized more clearing of FX derivatives to remove bilateral CVA risk."
        }
      </li>
    </ul>

    <p className="article-subheading">Liquidity risk</p>
    <p>{"The risk that one cannot transact or liquidate positions without significant cost."}</p>
    <ul className="article-list">
      <li>
        {
          "Market Liquidity Risk: In major FX pairs, liquidity is usually ample, but it can evaporate in crises or off-hours. Participants manage this by maintaining appropriate position sizes relative to market depth. E.g. a hedge fund won't build a huge position in an emerging currency where the market is thin without expecting difficulty exiting. Traders set position limits that consider liquidity (tight limits for exotic currencies). Stop-loss orders and options can provide some protection if market gaps (though stops might get filled at worse levels in a gapping market)."
        }
      </li>
      <li>
        {
          "Central banks sometimes provide liquidity indirectly by stepping in during dysfunction (e.g. Fed swap lines ensure USD liquidity globally, reducing panic demand). For individual firms, having access to multiple execution venues and counterparties mitigates liquidity risk – if one LP withdraws quotes, they can still trade with others."
        }
      </li>
      <li>
        {
          "Funding Liquidity Risk: Ensuring the ability to meet cash obligations, such as margin calls or settlement payments. E.g. in a volatile FX move, a hedge fund might get a margin call from its PB – it needs readily available cash or lines of credit to meet it, or else positions will be cut at a possibly inopportune time. Firms manage this by keeping buffer cash or credit lines. Corporates ensure they have cash in the right currency accounts by settlement day (often facilitated by doing FX swaps to bridge timing gaps)."
        }
      </li>
      <li>
        {
          "Banks manage intra-day liquidity for settlements – e.g. ensuring they have enough of each currency in CLS by funding deadlines. If a currency is scarce, they might borrow from another bank or central bank for a day. The Basel III LCR (Liquidity Coverage Ratio) forces banks to hold high-quality liquid assets enough to cover 30-day outflows, which includes FX swap payables, etc., thus formalizing liquidity risk management."
        }
      </li>
    </ul>

    <p className="article-subheading">Operational risk</p>
    <p>
      {
        "The risk of loss due to failed processes, human error, system failure, or fraud. These are pretty obvious; fat finger, rogue employee, etc."
      }
    </p>

    <p className="article-subheading">Compliance Risk</p>
    <ul className="article-list">
      <li>
        {
          "Fixing manipulation: Banks have compliance processes to handle fixes now. They no longer allow sharing of fix order info among dealers at different banks (Global Code strongly forbids this information sharing beyond what's necessary)."
        }
      </li>
      <li>
        {
          "Last Look abuse: Compliance monitors reject ratios and hold times. The Code says last look should be \"transparent and fair\". If a bank systematically only rejects when trade would be bad for them but fills when it's good (asymmetric last look), that's considered misconduct. Many liquidity providers publish their fill ratios."
        }
      </li>
      <li>
        {
          "AML/Sanctions: In cross-border payments, ensuring currencies aren't being used for laundering is big. Banks use sanctions screening on payment messages (e.g. halt any payments involving sanctioned entities). FinCEN, OFAC, etc., enforce heavy fines if banks facilitate forbidden flows. E.g. banks had to ensure after Russia sanctions in 2022 that they don't trade with designated Russian banks or help clients circumvent ruble restrictions."
        }
      </li>
    </ul>

    <p className="article-subheading">Post-2008 reforms:</p>
    <ul className="article-list">
      <li>
        {
          "Basel III's Leverage Ratio makes sure even off-balance sheet FX exposures count in leverage calculations (through add-ons). Initially, it was harsh (counted full notional of derivatives), but now uses standardized exposure method (less punitive for short-dated FX)."
        }
      </li>
      <li>
        {
          "Capital Buffers: Banks hold capital for market risk (VaR or stressed VaR), for credit risk (counterparty risk on derivatives), and for operational risk (basel op risk charge)."
        }
      </li>
      <li>
        {
          "The BIS monitors banks' FX settlement exposures via periodic surveys and has pushed banks to improve PvP adoption. Supervisors may require banks that still settle a lot outside CLS to hold more capital or liquidity against that settlement exposure."
        }
      </li>
    </ul>

    <p className="article-heading">Settlement infrastructure</p>

    <p className="article-subheading">CLS</p>
    <p>
      {
        "TLDR; CLS is an atomic settlement method for payments. It nets out trades letting ~60-70 banks settle <2% of their gross trades."
      }
    </p>
    <p>
      {
        "If there is no direct account relationship between two operators on the foreign exchange market, foreign exchange transactions are traditionally settled via correspondent banks. In this case, when one party meets its obligation irrevocably, it does so without knowing whether its counterparty will settle its liability. This means that there is a risk of the counterparty delivering late (liquidity risk) or, in the worst case, not at all (credit risk). These risks are compounded if the foreign exchange market participants are in different time zones."
      }
    </p>
    <p>
      {
        "The famous case of this was Herstatt bank; tldr, in 1974, a German bank FTD'd 2b German Marks, and due to timezone differences (USD sent in exchange for Marks settled before the Marks were to be sent the following day), their counterparty failed to receive their USD payments. This led to a slow system for ~20yrs where there there was a third party escrow system, eliminating principal risk."
      }
    </p>

    <figure className="article-figure">
      <img
        src="/writing/fx-market-structure/conventional-settlement.png"
        alt="Conventional settlement of a foreign exchange transaction across correspondent banks in Japan and the USA."
      />
      <figcaption>
        Chart 1: Conventional settlement of a foreign exchange transaction.
      </figcaption>
    </figure>
    <p>
      {
        "Participation in CLS can be either direct (settlement member) or indirect (third party). There are ~60 settlement member banks. All must have an equity stake in CLS. The important features for these banks is as follows:"
      }
    </p>
    <ul className="article-list">
      <li>{"Account with CLS"}</li>
      <li>{"18 currency-specific subaccounts"}</li>
      <li>
        {
          "self-collatrealizing transactions; ie, a liability in one currency is always covered by a claim in another currency"
        }
      </li>
    </ul>
    <p>
      {
        "In theory, this is pretty easy; every party sends the currency they sell into CLS, and it settles both sides. But t+2 is liquidity inefficient. To solve this, settlement is separated from payment flows; meaning that they are settled on a gross basis, but then payment is netted. Meaning that only the net positions of a settlement member is transferred. CLS claims that ~2% of gross amounts are ever actually settled in cash."
      }
    </p>
    <p>{"In practical terms, for each FX trade, the operational flow is:"}</p>
    <ol className="article-list">
      <li>
        {
          "Trade Confirmation: Right after execution, counterparties (or their prime brokers) confirm the trade details, often through electronic messaging (SWIFT MT300 or protocols like FIX). Many use services like MarkitSERV or GTX Post-Trade to match details. Misconfirmation is an operational risk, though nowadays STP means trade tickets go straight from platform to back-office systems."
        }
      </li>
      <li>
        {
          "Netting: If a bank has 100 trades today with another bank, they will net all the USD pays and receives into one net USD amount, same for EUR, etc., typically by value date."
        }
      </li>
      <li>
        {
          "Settlement Instruction: If using CLS, both submit the instructions to CLS (or via a third-party member). If not, they send SWIFT MT202 payment orders through correspondent banks. SWIFT gpi gives a UETR (unique end-to-end ID) to track."
        }
      </li>
      <li>
        {
          "Funding: On settlement date, CLS will require each member to pay in the currency they owe by a certain cutoff (during a window around 6–9am London depending on currency). For non-CLS, parties must ensure they have funds in their correspondent account. Some markets require prefunding for NDFs (e.g. in certain clearinghouses)."
        }
      </li>
      <li>
        {
          "PvP/Finality: In CLS, once all pay-ins received, it simultaneously pays out the currencies to the respective receivers – completing the chain. Outside CLS, each payment is final when settled in that currency's RTGS (Fedwire, etc.), but without PvP the two legs are not linked."
        }
      </li>
    </ol>
    <figure className="article-figure">
      <img
        src="/writing/fx-market-structure/pvp-settlement.png"
        alt="Settlement of a foreign exchange transaction via a payment-versus-payment (PvP) intermediary."
      />
      <figcaption>
        Chart 2: Settlement of a foreign exchange transaction in accordance
        with the payment-versus-payment principle. The two banks involved (or
        their correspondent banks) transfer the payment liabilities to an
        intermediate institution (intermediary). Only when both currencies have
        arrived at the intermediary are they then passed on to the relevant
        banks by the intermediary. With this PvP mechanism principal risk is
        eliminated.
      </figcaption>
    </figure>

    <p>{"As such CLS:"}</p>
    <ul className="article-list">
      <li>
        {"Direct central-bank access removes credit/liquidity exposure to a single commercial bank"}
      </li>
      <li>{"Preserves trade data confidentiality"}</li>
    </ul>
    <p>
      {
        "The Swiss National Bank has interest-free intraday liquidity to repo CHF deposits with collateral, as do other banks for the purposes of liquidity management"
      }
    </p>
    <p>
      {
        "Missed payments can cascade (one bank's failure to pay USD in CLS could force counterparties to scramble for USD). CLS has loss-sharing agreements and central bank backstops to handle member failure, and indeed has managed past incidents (e.g. Lehman's default in 2008) without losses to others."
      }
    </p>
    <p>{"Other quick facts:"}</p>
    <ul className="article-list">
      <li>
        {"CLS is continuous linked settlement; a nonprofit company handling instantaneous settlement formed in 2002"}
      </li>
      <li>{"ATH volume of $15.4t in one day, ATH of 3.2m trades in one day. Average ~$7t/d in 2024"}</li>
      <li>{"Settles 51% of global FX transactions; only 18 currencies"}</li>
      <li>
        {"17% of global FX volume are on currencies which are non-CLS tradeable "}
        <a
          href="https://www.bankofcanada.ca/wp-content/uploads/2024/10/cls-presentation.pdf"
          target="_blank"
          rel="noreferrer"
        >
          source
        </a>
        <ul className="article-list">
          <li>{"Most of this is RMB; 7% of global FX turnover"}</li>
        </ul>
      </li>
    </ul>
    <p>
      {
        "FX settlement risk is a focal issue. Approximately $2.2 trillion of daily FX turnover (nearly 30% of trades) remains settled without payment-versus-payment (PvP) protection, leaving participants exposed to counterparty default risk. Even including CLS, settlement risk in FX has grown in absolute terms, spurring calls for broader PvP adoption and new solutions (on-chain PvP?). Meanwhile, faster payment initiatives (e.g. SWIFT gpi) are improving post-trade processing and transparency of cross-border flows."
      }
    </p>

    <p className="article-subheading">Other innovations</p>
    <p>
      {
        "SWIFT gpi and Payment Reforms: While not specific to FX trading, the SWIFT gpi (global payments innovation) initiative has indirectly improved FX settlement transparency. gpi provides end-to-end tracking of cross-border payments and ensures faster credit (most gpi payments reach beneficiary same-day). For FX, this means when a trade's currency leg is paid via SWIFT gpi, both parties can track it in real time, reducing uncertainty about payment status. Many banks have adopted MT 300 series confirmations and SWIFT gpi for FX, shortening the settlement cycles. Some markets are moving to T+1 or T+0 for certain trades (e.g. USD/CAD already T+1; India NDFs often settled T+0 in USD). The U.S. is shifting equities to T+1 in 2024, which means any FX associated (like USD to settle a foreign investor's trade) also speeds up, increasing FX volume on short tenor."
      }
    </p>
    <p>
      {
        "Bilateral Netting and On-Us Settlement: Even outside CLS, banks mitigate risk by bilateral netting agreements – before settlement date, counterparties net all their payables/receivables in a given currency to one net payment. The BIS noted that presettlement netting reduced about $1.3T of gross payments per day in 2022. Banks also do \"on-us\" settlement: if two customers of the same bank traded FX, the bank can settle it internally on its books (no external payment needed). If those on-us settlements happen with proper controls (or internal PvP across accounts), risk is minimal. But the BIS pointed out some on-us settlements may not have full simultaneous exchange if not properly synchronized."
      }
    </p>

    <p className="article-heading">How does a neo-Fintech company (PSP's, Wise, etc.) do FX?</p>
    <p>
      {
        "TLDR; PSPs like Wise, Revolut and Stripe deliver low-fee, near-mid-market cross-border FX by matching flows locally, auto-hedging net positions and wiring via API-driven local rails."
      }
    </p>
    <p>
      {
        "When you send money A→B, PSPs either net against opposite flows in their regional accounts or hedge any net exposure in the interbank market. They run a pre-funded model (so there's no customer credit), plug into open-banking and liquidity APIs, and trigger local payouts via Faster Payments, ACH, SWIFT gpi, etc."
      }
    </p>
    <p>{"FX Execution Models:"}</p>
    <ul className="article-list">
      <li>
        {
          "Intraday & End-of-Day Netting PSPs group thousands of small customer flows into a single net position per currency pair—often on an hourly or daily cycle—then execute one wholesale FX trade to offset the net imbalance. This slashes both the number and value of trades sent to the interbank market, cutting execution costs and settlement risk"
        }
      </li>
      <li>
        {
          "Smart Order Routing & Algorithmic Slicing When netting leaves residual exposures, PSPs deploy smart order routers that split (\"slice\") the remaining order into child orders, distributing them across multiple ECNs and bank APIs. Execution strategies like TWAP or VWAP help minimize market impact and aggregate liquidity across fragmented pools"
        }
      </li>
      <li>
        {
          "Real-Time Back-to-Back Hedging Some PSPs guarantee every customer conversion is immediately hedged: upon customer execution, an API call simultaneously executes an offsetting FX trade in the interbank market. This back-to-back model locks in mid-market price for the user and leaves zero directional FX exposure on the PSP's books"
        }
      </li>
      <li>
        {
          "Dynamic Threshold Hedging PSPs set currency-specific exposure limits using a \"one-day expected loss\" framework and absolute risk appetites. Automated hedging engines monitor live net positions and trigger market trades as soon as thresholds are breached, keeping volatility risk within predefined buffers."
        }
      </li>
      <li>
        {
          "Prime Brokerage & Multilateral Settlement At scale, PSPs forge prime-broker relationships and access CLS for PvP finality. They connect to multiple wholesale banks and ECNs, enabling them to internally match or externally hedge via prime brokers, and settle net positions through CLS to eliminate principal risk"
        }
      </li>
    </ul>
    <p>{"Technology & Automation:"}</p>
    <ul className="article-list">
      <li>{"Open Banking APIs to pull in customer funds instantly"}</li>
      <li>{"Streaming Liquidity APIs to source competitive FX quotes"}</li>
      <li>
        {"Automated Routing"}
        <ol className="article-list">
          <li>{"Check internal pools"}</li>
          <li>{"If short, hit the market"}</li>
          <li>{"Trigger local payout and ledger updates—all in seconds"}</li>
        </ol>
      </li>
    </ul>
    <p>{"Credit & Risk Management:"}</p>
    <ul className="article-list">
      <li>{"Pre-funded model: customers pay first, so PSPs hold no receivable"}</li>
      <li>{"Fixed-rate windows: guarantee a quote for a few seconds/minutes"}</li>
      <li>{"Buffering: small spread built in to cover micro-volatility"}</li>
    </ul>
    <p>{"Regulatory & Infrastructure:"}</p>
    <ul className="article-list">
      <li>{"Licensed as EMIs or money transmitters (not banks)"}</li>
      <li>{"Heavy investment in AML/KYC screening tech"}</li>
      <li>{"Direct rails: Faster Payments, ACH, SEPA, SWIFT gpi (via sponsor banks)"}</li>
      <li>{"Optional CLS access for high-volume members to get PvP finality"}</li>
    </ul>
    <p>{"Banks vs. PSPs:"}</p>
    <ul className="article-list">
      <li>{"Risk appetite: PSPs avoid proprietary FX positions; banks often warehouse risk"}</li>
      <li>{"Product scope: PSPs focus on spot and simple forwards (SME forwards require collateral)"}</li>
      <li>{"Pricing: Near-mid-market + explicit fee vs. wider bank spreads & hidden markups"}</li>
    </ul>

    <p className="article-heading">How do banks do FX?</p>
    <p>
      {
        "Following 2008, there was a rule in the Dodd-Frank Act to reduce risky behaviour by banks and protect depositors — called the Volcker Rule, it prevents banks' investment in hedge funds and private equity funds, but also prohibiting them from engaging in prop trading. Hence, the origin of PFOF."
      }
    </p>
    <p>
      {"Consider a corporate client wants to buy GBP/USD 50 million. The typical workflow:"}
    </p>
    <ol className="article-list">
      <li>
        {
          "The client contacts the bank's sales desk (via a platform or chat). At major banks, corporate and real-money flow is handled by a salesperson who interfaces with traders."
        }
      </li>
      <li>
        {
          "The salesperson sees the request and may use the bank's internal pricing engine to get a rate. Big banks have automated pricing for most standard trades up to a certain size: the engine takes in live market data (ECN prices, etc.), adds a margin based on client tier/size, and outputs a quote often in milliseconds. For very large or less liquid pairs, the salesperson will voice it to a trader on the FX trading desk."
        }
      </li>
      <li>
        {
          "The bank executes the trade with the client at the agreed price. Immediately, the bank has an open position (long GBP, short USD in this example)."
        }
      </li>
      <li>
        {
          "Risk management: The trader (e.g. GBP/USD spot trader) will decide how to hedge. They might offset by trading with another client or on the market. If the bank has a flow internalization system, it might automatically match this client's GBP buy with another client's GBP sell (if one came in around same time). If not, the trader could go to EBS or another bank to sell GBP and square the position. Alternatively, the trader might choose to hold the position for a bit, speculating that price will move favorably (this is where the bank can make or lose money beyond the spread)."
        }
      </li>
      <li>
        {
          "Post-trade: The trade is confirmed, recorded in the bank's systems. Back-office ensures settlement instructions go out (possibly through CLS if with another CLS member)."
        }
      </li>
      <li>
        {
          "Profit: The bank's profit on that trade is roughly the spread charged plus any market move benefit when hedging. Dealers try to keep overall positions balanced but also take views. They operate under risk limits (e.g. a daily VAR limit, maximum open position in each currency)."
        }
      </li>
    </ol>
    <p>
      {
        "Banks deploy pricing engines that stream quotes to various platforms (EBS, FXall, 360T, etc.) simultaneously. For instance, a bank's eFX engine connects via FIX API to FXall to auto-respond to client RFQs within milliseconds, and to ECNs with limit orders or streaming quotes. Banks often have co-located servers near exchange matching engines for speed. They use quant models to skew prices depending on market conditions and internal inventory – e.g. if a bank is long a lot of EUR, its engine may shade the EUR offer price a tiny bit lower to encourage clients to buy EUR from it, reducing its inventory (this is called skewing or internalization bias)."
      }
    </p>

    <p className="article-heading">How does Interactive Brokers do FX?</p>
    <p>
      {
        "Major banks developed internal matching systems (internalization engines) to cross client flows internally rather than show every order to the market – this reduces information leakage and can give clients better fills. The BIS noted that a greater share of FX volume is being internalized or traded on \"disclosed\" relationship venues, rather than on the anonymous primary markets. This suggests that liquidity is plentiful but fragmented – no single venue has a majority market share; instead, dozens of venues and bank internal pools contribute."
      }
    </p>
    <p>
      {
        "To handle this, many institutions use aggregation software that pulls quotes from multiple sources (EBS, Cboe, Currenex, etc. for banks; or multiple bank APIs for a buy-side) and executes at the best price. Aggregators might also split a large order among venues. Smart order routing technology has thus become crucial in FX, akin to how equity algos route to different exchanges. Interactive brokers is one such venue."
      }
    </p>

    <p className="article-heading">Corporate Treasuries</p>

    <p className="article-subheading">Large MNC's</p>
    <p style={{ fontWeight: 600 }}>{"Exposure Management"}</p>
    <p>
      {
        "MNCs encounter a variety of FX exposures due to their global operations, which can be categorized into three main types:"
      }
    </p>
    <ul className="article-list">
      <li>
        {
          "Transaction Exposure: This arises from specific, known cash flows in foreign currencies, such as a European subsidiary expecting a $10 million payment from a U.S. client in six months. Currency fluctuations could erode the value of this receivable if left unhedged."
        }
      </li>
      <li>
        {
          "Translation Exposure: When consolidating financial statements, MNCs must convert foreign subsidiaries' earnings into the parent company's reporting currency (e.g., converting EUR profits into USD). A strengthening reporting currency can reduce reported earnings, even if operational performance remains strong."
        }
      </li>
      <li>
        {
          "Economic Exposure: This is a longer-term risk, reflecting how FX movements affect an MNC's competitiveness. For instance, if a competitor's home currency weakens, they might lower prices, forcing the MNC to respond strategically."
        }
      </li>
    </ul>
    <p>
      {"To address these exposures, MNCs establish a formal FX risk management policy specifying:"}
    </p>
    <ul className="article-list">
      <li>
        {"Which exposures to hedge (e.g., prioritizing transaction and translation risks over economic ones)."}
      </li>
      <li>
        {
          "Target hedge ratios (e.g., hedging 70% of forecasted cash flows within 12 months, tapering to 30% for 24-month forecasts)."
        }
      </li>
      <li>{"Approved instruments (e.g., forwards, swaps, or options)."}</li>
      <li>{"Execution guidelines, including delegation of authority and monitoring protocols."}</li>
    </ul>
    <p>
      {
        "For forecasted exposures, such as anticipated sales in a foreign currency, MNCs adopt a layered hedging approach. Hedge ratios increase as forecasts become more certain—perhaps starting at 25% for a 12-month forecast and rising to 80% as the date nears. For firm commitments, like a signed contract payable in JPY, MNCs often hedge nearly 100% to eliminate uncertainty, ensuring predictable cash flows."
      }
    </p>
    <p style={{ fontWeight: 600 }}>{"Execution Workflows and Technology"}</p>
    <ul className="article-list">
      <li>
        {
          "Treasury Management Systems (TMS): MNCs rely on platforms like Kyriba, SAP Treasury, or Reval to streamline their treasury operations. These systems allow treasurers to input exposure forecasts from subsidiaries, track positions in real time, and generate hedge recommendations. For instance, a TMS might flag a $50 million EUR exposure across multiple subsidiaries, consolidating it into a single hedge executed centrally."
        }
      </li>
      <li>
        {
          "Multi-bank Platforms: To secure competitive pricing, MNCs connect their TMS to trading platforms like FXall, 360T, or Bloomberg FX. A treasurer might initiate a request for quote (RFQ) on FXall for a €50 million forward contract, receiving bids from five banks within seconds. Once a trade is executed, details flow back into the TMS via straight-through processing (STP), minimizing manual errors and enhancing efficiency."
        }
      </li>
      <li>
        {
          "Bank Relationships: MNCs maintain relationships with a portfolio of banks—often 5 to 10 global players like JPMorgan, Citi, or HSBC—to diversify counterparty risk and ensure favorable terms. These relationships are formalized through annual bank mandates or requests for proposals (RFPs), where FX business is allocated in exchange for competitive pricing or credit support. Banks, in turn, provide credit lines for derivatives as part of broader financing packages, such as revolving credit facilities."
        }
      </li>
      <li>
        {
          "Internal Netting: To reduce external hedging costs, many MNCs operate an internal netting center. For example, if a German subsidiary has a $10 million receivable and a U.S. subsidiary has a $10 million payable, the treasury nets these internally, hedging only residual exposures with external markets. Systems like Kyriba automate these netting cycles, typically monthly or quarterly, cutting transaction volumes and fees."
        }
      </li>
      <li>
        {
          "Pricing and Benchmarks: MNCs demand transparency in pricing, often benchmarking bank quotes against WM/Reuters fixing rates (e.g., the 4pm London fix). For large trades, they might use fixing orders to execute at a specific rate, avoiding market slippage, or algorithmic execution to spread trades over time for optimal pricing. Tools like ITG or BestX provide transaction cost analysis (TCA), enabling treasurers to evaluate trade performance and negotiate better spreads."
        }
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Hedging Instruments"}</p>
    <p>{"MNCs favour instruments that balance simplicity, cost, and effectiveness:"}</p>
    <ul className="article-list">
      <li>
        {
          "Forwards: The workhorse of FX hedging, forwards lock in exchange rates for future delivery, ideal for firm commitments or high-certainty forecasts. For example, a forward might secure a USD/EUR rate for a $20 million payment due in six months."
        }
      </li>
      <li>
        {
          "Swaps: Used to manage cash flow timing or roll over hedges, swaps are common when MNCs need flexibility in settlement dates."
        }
      </li>
      <li>
        {
          "Options: These provide downside protection without obligation, useful for uncertain exposures like forecasted sales that might not materialize. A collar—an option strategy combining a put and call—might be used to lock in a budget rate, a tactic popular in industries like aviation where FX and commodity prices (e.g., fuel) are hedged together."
        }
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Platform Interactions"}</p>
    <ul className="article-list">
      <li>
        {"Multi-bank platforms like FXall excel for competitive pricing on vanilla trades (e.g., forwards and swaps)."}
      </li>
      <li>
        {
          "Single-bank platforms are preferred for quick, small trades or when relationship banks offer tighter spreads on emerging market currencies (e.g., BRL or INR)."
        }
      </li>
      <li>
        {
          "SWIFT connectivity streamlines post-trade processes. Many MNCs join SWIFT's SCORE network, receiving trade confirmations directly from banks and issuing payment instructions efficiently, reducing operational risk."
        }
      </li>
    </ul>

    <p className="article-subheading">SME's</p>
    <ul className="article-list">
      <li>
        {
          "Unlike MNCs, SMEs rarely have ISDA agreements or multi-bank credit lines, limiting their ability to use derivatives or negotiate favorable terms."
        }
      </li>
      <li>
        {
          "Historically High Costs: In traditional banking models, SMEs faced wide spreads—sometimes 200 pips on major currency pairs versus interbank spreads of 2 pips—due to their smaller trade sizes and weaker bargaining power."
        }
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Fintech-Driven Solutions"}</p>
    <ul className="article-list">
      <li>
        {
          "Online Platforms: Companies like Wise Business, OFX, WorldFirst, and Revolut Business provide SMEs with spot FX and forward contracts via intuitive interfaces. These platforms often require pre-funding (e.g., depositing funds upfront) or a small margin for forwards, accommodating SMEs without established credit lines."
        }
      </li>
      <li>
        {
          "Automated Hedging: Solutions like HedgeQuick or Kantox (acquired by BNP Paribas) simplify the process further. An SME inputs its exposures—say, $100,000 in expected USD revenue—and the platform automatically executes a forward contract based on predefined risk preferences, reducing the need for manual oversight."
        }
      </li>
      <li>
        {
          "Simplified TMS: Traditional TMS providers like Kyriba have launched SME-friendly versions with basic features, while banks increasingly white-label fintech tools to enhance their SME offerings. For example, a regional bank might partner with Kantox to provide automated hedging to its clients."
        }
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Credit and Cost Considerations"}</p>
    <p>{"While SMEs still face higher costs than MNCs, fintech competition has narrowed the gap:"}</p>
    <ul className="article-list">
      <li>
        {
          "Spreads: Modern platforms offer SMEs spreads of 20-50 pips on minor pairs (e.g., USD/MXN) and under 10 pips on majors (e.g., EUR/USD) for moderate-sized trades, a vast improvement over the 100+ pips charged by banks historically."
        }
      </li>
      <li>
        {
          "Credit Options: SMEs with solid financials might secure modest FX credit lines from banks, enabling unsecured forwards. Alternatively, they can use collared forwards (limiting upside and downside) or funded forwards (pre-paying a portion of the trade) to manage credit constraints, though these increase costs slightly."
        }
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Education and Advisory Services"}</p>
    <p>
      {
        "Many SMEs lack the knowledge to navigate FX markets effectively, sometimes leading to risky decisions—like avoiding hedges based on market predictions. To bridge this gap:"
      }
    </p>
    <ul className="article-list">
      <li>
        {
          "Educational Resources: Fintechs and banks offer webinars, guides, and calculators to demystify hedging. For instance, OFX might host a session on \"Why Hedging Matters for SMEs,\" illustrating how a 10% currency swing could wipe out profit margins."
        }
      </li>
      <li>
        {
          "Many SME's use speculative spot because they don't know about forward contracts. One can buy forwards with a locked rate in order to hedge the date that they receive an invoice, for example. "
        }
        <a
          href="https://moneytransfercomparison.com/forward-contracts/"
          target="_blank"
          rel="noreferrer"
        >
          more details
        </a>
      </li>
    </ul>
    <p style={{ fontWeight: 600 }}>{"Practical Example: Hedging with an NDF"}</p>
    <p>
      {
        "Consider a small Argentine winery exporting wine to the U.S., expecting $500,000 in revenue in three months. With the Argentine peso (ARS) prone to devaluation, but the USD also volatile, the winery seeks to lock in its ARS proceeds. Using a fintech platform like WorldFirst, it books a USD/ARS non-deliverable forward (NDF). This contract sets a forward rate—say, 1 USD = 300 ARS—without physical delivery of ARS. At maturity, if the spot rate is 1 USD = 320 ARS, the winery receives a USD payout for the 20 ARS difference per dollar, securing its revenue in ARS terms. Such instruments, once inaccessible to SMEs due to complexity and cost, are now viable through fintech, typically costing a modest fee (e.g., 0.5% of notional value)."
      }
    </p>

    <p className="article-heading">Existing crypto solutions</p>
    <p>
      {
        "Projects like IBM's World Wire and JPMorgan's Liink/Onyx network have explored using blockchain tokens representing central bank money to do PvP for currency trades. The idea is to have tokens for each currency on a ledger and atomic swap them. Project Ion/DTCC and the BIS's prototypes (e.g. Nexus and mCBDC Bridge) also target cross-currency atomic settlement using either central bank digital currencies (CBDCs) or tokenized deposits."
      }
    </p>
    <p>
      {
        "Fintechs like Baton Systems have built a platform to coordinate FX settlements. Baton interfaces with banks' existing systems and uses a distributed ledger to allow participants to see trades, net them, and then synchronize payments through RTGS systems. Essentially, Baton overlays a PvP logic without needing a central entity like CLS – it orchestrates bilaterally. This can be used for currencies not in CLS or for participants that want flexibility. It's reported that some banks are testing such systems for bilateral PvP on demand, potentially reducing trapped liquidity (since CLS has a single daily settlement window, whereas bilateral arrangements could settle multiple times per day)."
      }
    </p>

    <p className="article-heading">My open questions</p>
    <ol className="article-list">
      <li>
        {
          "There seems to be an obvious gap for \"CLS but more open/democratized\" but how do we actually appeal to eg. Xiaomi, which has its own CFO which presumably does payments/makes them more efficient?"
        }
        <ol className="article-list">
          <li>{"As an extension, what made Amazon use Stripe when they had their own solution already?"}</li>
          <li>{"How much money do they lose on not buying forwards for their inventory"}</li>
        </ol>
      </li>
      <li>{"Devil's advocate VC question: CLS is commoditized; why not us?"}</li>
      <li>
        {"Why aren't more companies/users using CLS? It's obviously better/cheaper; is it:"}
        <ol className="article-list">
          <li>
            {"bad incentives — banks can make money as is so they still price FX at 2.5% so they don't lose their margin"}
          </li>
          <li>{"bad marketing — they don't know it exists"}</li>
          <li>{"then: why does Wise not use CLS through its partner banks when it loses so much on hedging?"}</li>
        </ol>
      </li>
    </ol>

    <p className="article-heading">Insights</p>
    <ol className="article-list">
      <li>
        {"Banks are at a fundamental disadvantage due to not being able to have a prop desk due to the Volcker Rule"}
      </li>
    </ol>

    <p className="article-heading">Glossary</p>
    <ul className="article-list">
      <li>{"PvP: Payment versus Payment; essentially an escrow service"}</li>
      <li>{"SMB/SME: Small-Medium Business/Enterprise"}</li>
      <li>{"MNC: Multinational Company"}</li>
      <li>{"PFOF: Payment for order flow"}</li>
      <li>
        {
          "Last Look: A liquidity provider, upon receiving a trade request, has a short window to accept or reject the trade if price moved or fails credit check. Meant to avoid stale pricing trade, but can be abused if misused."
        }
      </li>
      <li>{"VaR: Value at risk"}</li>
      <li>
        {
          "NDF: Non-deliverable forward, OTC derivative to settle the difference between a forward rate and spot rate at a future date without delivery of the underlying currency from either party"
        }
      </li>
    </ul>

    <p className="article-heading">References</p>
    <ul className="article-list">
      <li>
        <a href="https://www.bis.org/statistics/rpfx22_fx.pdf" target="_blank" rel="noreferrer">
          Bank for International Settlements – Triennial Central Bank Survey 2022
        </a>
        {": Global FX volume and instrument breakdown; currency shares and trading center data."}
      </li>
      <li>
        <a
          href="https://www.reuters.com/markets/us/global-fx-trading-hits-record-75-trln-day-bis-survey-2022-10-27/"
          target="_blank"
          rel="noreferrer"
        >
          Reuters – "Global FX trading hits record $7.5 trln a day – BIS survey"
        </a>
        {" (Oct 2022): USD in 88% of trades; RMB rising to 7%; London's market share dip and Singapore's rise."}
      </li>
      <li>
        <a href="https://www.bis.org/publ/qtrpdf/r_qt2212i.htm" target="_blank" rel="noreferrer">
          BIS Quarterly Review (Dec 2022) – "FX settlement risk: an unsettled issue"
        </a>
        {": ~$2.2T at risk daily, ~30% of deliverable FX turnover lacks PvP."}
      </li>
      <li>
        <a
          href="https://www.euromoney.com/article/2dpv1vyj13vl7ipcprfgg/awards/euromoney-fx-awards/the-worlds-best-fx-clearing-and-settlement-venue-cls"
          target="_blank"
          rel="noreferrer"
        >
          Euromoney FX Awards 2024 – industry trends
        </a>
        {": CLS settlement averages $7T daily; 360T's growth in swaps (NDF volumes)."}
      </li>
      <li>
        <a
          href="https://www.fx-markets.com/infrastructure/7948449/fx-settlement-risk-grows-again-in-latest-triennial-survey"
          target="_blank"
          rel="noreferrer"
        >
          Global FX Code (2017, updated 2021)
        </a>
        {" – principles on last look, information sharing."}
      </li>
      <li>
        <a
          href="https://www.isda.org/a/p7cgE/The-Way-Forward-for-Sustainability-linked-Derivatives.pdf"
          target="_blank"
          rel="noreferrer"
        >
          International Swaps and Derivatives Association (ISDA) – guidance on Sustainability-linked Derivatives.
        </a>
      </li>
      <li>
        <a
          href="https://www.bis.org/about/bisih/topics/fmis/meridian_fx.htm"
          target="_blank"
          rel="noreferrer"
        >
          BIS Innovation Hub
        </a>
        {" – reports on multi-CBDC experiments (Project mBridge, Jura) demonstrating cross-border FX PvP with digital currencies."}
      </li>
      <li>
        <a
          href="https://www.cftc.gov/sites/default/files/idc/groups/public/%40newsroom/documents/file/fd_qa_final.pdf"
          target="_blank"
          rel="noreferrer"
        >
          CFTC and ESMA regulations
        </a>
        {" – various releases on FX forwards exemption, leverage rules for retail FX."}
      </li>
      <li>
        <a
          href="https://www.ecb.europa.eu/paym/groups/pdf/fxcg/2023/20230622/Item_2_Presentation_by_GFMA_and_OMG_and_SSM.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Global Financial Markets Association
        </a>
        {" and "}
        <a href="https://www.gfma.org/policies-resources/gfma-fx-global-code/" target="_blank" rel="noreferrer">
          this
        </a>
        {" – reports on FX market clearing and settlement improvements (advocating expansion of PvP)."}
      </li>
    </ul>
  </div>
);

const articles: Record<string, React.FC> = {
  "rate-varying-poisson-mmpp": RateVaryingArticle,
  "anticlustering-multi-objective-optimization": AnticlusteringArticle,
  "fx-market-structure": FXMarketStructureArticle,
};

const Article = () => {
  const defaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [theme, setTheme] = useLocalStorage(
    "theme",
    defaultDark ? "dark" : "light"
  );

  const { slug } = useParams<{ slug: string }>();
  const ArticleBody = articles[slug];

  const switchTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  return (
    <div className="main" data-theme={theme}>
      <div className="mid-section">
        <div className="header">
          <button className="switcher" onClick={switchTheme}>
            {theme === "light" ? (
              <FontAwesomeIcon size={"2x"} icon={faMoon} className="icon" />
            ) : (
              <FontAwesomeIcon size={"2x"} icon={faSun} className="icon" />
            )}
          </button>
        </div>
        <div className="section">
          <div className="section-title">
            <p>Back</p>
          </div>
          <div className="section-text">
            <p>
              <Link className="link" to="/writing">
                ← All writing
              </Link>
            </p>
          </div>
        </div>
        <div className="section">
          <div className="section-title">
            <p>Article</p>
          </div>
          {ArticleBody ? (
            <ArticleBody />
          ) : (
            <div className="section-text">
              <p>Article not found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Article;
